import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Plus, Calendar } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EmployeeLeaves = () => {
  const { getAuthHeader } = useAuth();
  const [leaves, setLeaves] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    leave_type: 'casual',
    from_date: '',
    to_date: '',
    reason: ''
  });

  const fetchLeaves = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/employee/leave/requests`, {
        headers: getAuthHeader()
      });
      setLeaves(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching leaves:', error);
      }
    } finally {
      setLoading(false);
    }
  }, [BACKEND_URL, getAuthHeader]);

  useEffect(() => {
    fetchLeaves();
  }, [fetchLeaves]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${BACKEND_URL}/api/employee/leave/request`,
        formData,
        { headers: getAuthHeader() }
      );
      alert('Leave request submitted successfully!');
      setShowForm(false);
      setFormData({ leave_type: 'casual', from_date: '', to_date: '', reason: '' });
      fetchLeaves();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to submit leave request');
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Leave Requests</h2>
        <Button
          onClick={() => setShowForm(!showForm)}
          className="bg-primary hover:bg-primary/90"
        >
          <Plus size={20} className="mr-2" />
          Request Leave
        </Button>
      </div>

      {showForm && (
        <Card>
          <CardHeader>
            <CardTitle>New Leave Request</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Leave Type *</label>
                <select
                  value={formData.leave_type}
                  onChange={(e) => setFormData({ ...formData, leave_type: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  required
                >
                  <option value="casual">Casual Leave</option>
                  <option value="sick">Sick Leave</option>
                  <option value="earned">Earned Leave</option>
                </select>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">From Date *</label>
                  <Input
                    type="date"
                    value={formData.from_date}
                    onChange={(e) => setFormData({ ...formData, from_date: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">To Date *</label>
                  <Input
                    type="date"
                    value={formData.to_date}
                    onChange={(e) => setFormData({ ...formData, to_date: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Reason *</label>
                <Textarea
                  value={formData.reason}
                  onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                  required
                  rows={3}
                />
              </div>
              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-primary hover:bg-primary/90">
                  Submit Request
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Leave History</CardTitle>
        </CardHeader>
        <CardContent>
          {leaves.length > 0 ? (
            <div className="space-y-3">
              {leaves.map((leave) => (
                <div key={leave.id} className="border rounded-lg p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium">{leave.leave_type.charAt(0).toUpperCase() + leave.leave_type.slice(1)} Leave</span>
                        <Badge className={getStatusBadge(leave.status)}>
                          {leave.status.toUpperCase()}
                        </Badge>
                      </div>
                      <div className="text-sm text-gray-600 flex items-center gap-2">
                        <Calendar size={14} />
                        {new Date(leave.from_date).toLocaleDateString()} - {new Date(leave.to_date).toLocaleDateString()} ({leave.days} days)
                      </div>
                    </div>
                  </div>
                  <div className="text-sm text-gray-700 mb-2">
                    <strong>Reason:</strong> {leave.reason}
                  </div>
                  {leave.review_comments && (
                    <div className="text-sm text-gray-600 bg-gray-50 p-2 rounded">
                      <strong>HR Comment:</strong> {leave.review_comments}
                    </div>
                  )}
                  {leave.reviewed_by && (
                    <div className="text-xs text-gray-500 mt-2">
                      Reviewed by: {leave.reviewed_by}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">No leave requests yet</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default EmployeeLeaves;
